import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Badge,
  Divider,
  Banner,
  Box,
  Icon,
  ChoiceList,
} from '@shopify/polaris';
import { CheckSmallIcon } from '@shopify/polaris-icons';
import { useNavigate } from 'react-router-dom';
import { useShop } from '../context/ShopContext';
import { useAuthenticatedFetch } from '../hooks/useApi';
import { useEmbedded } from '../components/ShopifyAppProvider';

interface PlanDefinition {
  id: string;
  name: string;
  monthlyPrice: number;
  annualPrice: number;
  description: string;
  limit: string;
  features: string[];
  highlighted?: boolean;
  badge?: string;
}

export default function PricingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { shopInfo, refreshShopInfo } = useShop();
  const fetchWithAuth = useAuthenticatedFetch();
  const { isEmbedded } = useEmbedded();

  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annual'>('monthly');
  const [subscribingPlan, setSubscribingPlan] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const currentPlan = shopInfo?.planFeatures?.planName ?? 'Free';
  const currentLevel = shopInfo?.planFeatures?.planLevel ?? 0;
  const isAnnual = billingInterval === 'annual';

  const plans: PlanDefinition[] = [
    {
      id: 'free',
      name: t('pricing.starter.name'),
      monthlyPrice: 0,
      annualPrice: 0,
      description: t('pricing.starter.description'),
      limit: t('pricing.starter.limit'),
      features: [
        t('pricing.starter.features.orders'),
        t('pricing.starter.features.products'),
        t('pricing.starter.features.recommendations'),
        t('pricing.starter.features.widget'),
        t('pricing.starter.features.analytics'),
        t('pricing.starter.features.support'),
      ],
    },
    {
      id: 'growth',
      name: t('pricing.growth.name'),
      monthlyPrice: 29.90,
      annualPrice: 299.00,
      description: t('pricing.growth.description'),
      limit: t('pricing.growth.limit'),
      features: [
        t('pricing.growth.features.orders'),
        t('pricing.growth.features.everything'),
        t('pricing.growth.features.bundles'),
        t('pricing.growth.features.bought'),
        t('pricing.growth.features.analytics'),
        t('pricing.growth.features.support'),
        t('pricing.growth.features.chat'),
      ],
    },
    {
      id: 'professional',
      name: t('pricing.professional.name'),
      monthlyPrice: 49.90,
      annualPrice: 499.00,
      description: t('pricing.professional.description'),
      limit: t('pricing.professional.limit'),
      features: [
        t('pricing.professional.features.orders'),
        t('pricing.professional.features.everything'),
        t('pricing.professional.features.email'),
        t('pricing.professional.features.testing'),
        t('pricing.professional.features.api'),
        t('pricing.professional.features.support'),
        t('pricing.professional.features.training'),
      ],
      highlighted: true,
      badge: t('pricing.professional.badge'),
    },
    {
      id: 'enterprise',
      name: t('pricing.enterprise.name'),
      monthlyPrice: 79.90,
      annualPrice: 799.00,
      description: t('pricing.enterprise.description'),
      limit: t('pricing.enterprise.limit'),
      features: [
        t('pricing.enterprise.features.orders'),
        t('pricing.enterprise.features.everything'),
        t('pricing.enterprise.features.manager'),
        t('pricing.enterprise.features.sla'),
        t('pricing.enterprise.features.integration'),
        t('pricing.enterprise.features.billing'),
        t('pricing.enterprise.features.custom'),
      ],
    },
  ];

  const getPlanLevel = (planId: string): number => {
    switch (planId) {
      case 'free': return 0;
      case 'growth': return 1;
      case 'professional': return 2;
      case 'enterprise': return 3;
      default: return 0;
    }
  };

  const formatPrice = (plan: PlanDefinition): string => {
    if (plan.monthlyPrice === 0) return '$0';
    const price = isAnnual ? plan.annualPrice : plan.monthlyPrice;
    return `$${price.toFixed(2)}`;
  };

  const getPeriodLabel = (plan: PlanDefinition): string => {
    if (plan.monthlyPrice === 0) return '';
    return isAnnual ? t('pricingPage.perYear') : t('pricingPage.perMonth');
  };

  const getMonthlySavings = (plan: PlanDefinition): string | null => {
    if (!isAnnual || plan.monthlyPrice === 0) return null;
    const monthlyTotal = plan.monthlyPrice * 12;
    const savings = Math.round(((monthlyTotal - plan.annualPrice) / monthlyTotal) * 100);
    return t('pricingPage.savePercent', { percent: savings });
  };

  const handleSubscribe = useCallback(async (planId: string) => {
    setSubscribingPlan(planId);
    setError(null);

    try {
      const interval = billingInterval;
      const response = await fetchWithAuth<{
        planName: string;
        price: number;
        confirmationUrl?: string;
        isActive: boolean;
      }>(`/api/shopify/billing/subscribe?plan=${planId}&interval=${interval}`, {
        method: 'POST',
      });

      if (response.isActive) {
        // Free plan or already active — just refresh
        await refreshShopInfo();
        navigate('/app');
        return;
      }

      if (response.confirmationUrl) {
        // Redirect to Shopify's charge approval page
        if (isEmbedded) {
          window.open(response.confirmationUrl, '_top');
        } else {
          window.location.href = response.confirmationUrl;
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('pricingPage.error'));
    } finally {
      setSubscribingPlan(null);
    }
  }, [fetchWithAuth, refreshShopInfo, navigate, isEmbedded, billingInterval, t]);

  return (
    <Page
      title={t('pricingPage.title')}
      subtitle={t('pricingPage.subtitle')}
      backAction={{ content: t('common.back'), onAction: () => navigate('/app') }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Banner tone="info">
            <p>{t('pricing.guarantee')}</p>
          </Banner>
        </Layout.Section>

        {/* Billing interval toggle */}
        <Layout.Section>
          <InlineStack align="center">
            <ChoiceList
              title=""
              choices={[
                { label: t('pricingPage.monthly'), value: 'monthly' },
                { label: `${t('pricingPage.annual')} — ${t('pricingPage.savePercent', { percent: 17 })}`, value: 'annual' },
              ]}
              selected={[billingInterval]}
              onChange={(selected) => setBillingInterval(selected[0] as 'monthly' | 'annual')}
            />
          </InlineStack>
        </Layout.Section>

        {plans.map((plan) => {
          const planLevel = getPlanLevel(plan.id);
          const isCurrent = plan.name === currentPlan || (plan.id === 'free' && currentPlan === 'Free');
          const isDowngrade = planLevel < currentLevel;
          const isUpgrade = planLevel > currentLevel;
          const savings = getMonthlySavings(plan);

          return (
            <Layout.Section key={plan.id} variant="oneHalf">
              <Card>
                <BlockStack gap="400">
                  {/* Plan Header */}
                  <InlineStack align="space-between" blockAlign="center">
                    <BlockStack gap="100">
                      <InlineStack gap="200" blockAlign="center">
                        <Text as="h2" variant="headingLg">{plan.name}</Text>
                        {plan.badge && <Badge tone="success">{plan.badge}</Badge>}
                        {isCurrent && <Badge tone="info">{t('pricingPage.currentPlan')}</Badge>}
                        {savings && <Badge tone="attention">{savings}</Badge>}
                      </InlineStack>
                      <Text as="p" tone="subdued" variant="bodySm">{plan.description}</Text>
                    </BlockStack>
                  </InlineStack>

                  {/* Price */}
                  <InlineStack gap="100" blockAlign="end">
                    <Text as="span" variant="heading2xl" fontWeight="bold">{formatPrice(plan)}</Text>
                    <Text as="span" tone="subdued">{getPeriodLabel(plan)}</Text>
                  </InlineStack>

                  <Text as="p" variant="bodySm" tone="subdued">{plan.limit}</Text>

                  <Divider />

                  {/* Features */}
                  <BlockStack gap="200">
                    {plan.features.map((feature, index) => (
                      <InlineStack key={index} gap="200" blockAlign="center">
                        <Box>
                          <Icon source={CheckSmallIcon} tone="success" />
                        </Box>
                        <Text as="span" variant="bodySm">{feature}</Text>
                      </InlineStack>
                    ))}
                  </BlockStack>

                  <Divider />

                  {/* CTA */}
                  {isCurrent ? (
                    <Button disabled fullWidth>
                      {t('pricingPage.currentPlan')}
                    </Button>
                  ) : isDowngrade ? (
                    <Button
                      fullWidth
                      onClick={() => handleSubscribe(plan.id)}
                      loading={subscribingPlan === plan.id}
                      tone="critical"
                    >
                      {t('pricingPage.downgrade')}
                    </Button>
                  ) : (
                    <Button
                      fullWidth
                      variant="primary"
                      onClick={() => handleSubscribe(plan.id)}
                      loading={subscribingPlan === plan.id}
                    >
                      {isUpgrade ? t('pricingPage.upgrade') : t('pricingPage.selectPlan')}
                    </Button>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>
          );
        })}
      </Layout>
    </Page>
  );
}
